/**
 * Walk the shapes inside a PowerPoint group.
 *
 * Shared by DI-04 (preview the text inside a group) and, later, DI-03 find /
 * replace (G-6 phase 2), which the spec asks to recurse `group.shapes` with a
 * depth cap of 3. The walker only enumerates: what to do with each shape is
 * the caller's `visit` callback.
 *
 * `shape.group.shapes` needs PowerPointApi 1.8. The caller checks the API set;
 * load failures propagate so the caller decides how to report them.
 */

/** `shape.group` needs PowerPointApi 1.8. */
export const GROUP_SHAPES_API_SET = { name: "PowerPointApi", version: "1.8" } as const;

/** Nesting levels walked, counting the top group as level 1 (spec G-6). */
export const MAX_GROUP_DEPTH = 3;

export interface GroupWalkResult {
  /** True when a nested group sat beyond {@link MAX_GROUP_DEPTH} and was not opened. */
  readonly skippedDeeper: boolean;
  /** True when `visit` returned `false` and the walk ended before the last shape. */
  readonly stoppedEarly: boolean;
}

/**
 * Called for each non-group shape. Return `false` to stop the walk; any other
 * return value (including none) continues it. `groupPath` is the ids of the
 * groups holding the shape, outermost first.
 */
export type GroupShapeVisitor = (
  shape: PowerPoint.Shape,
  level: number,
  groupPath: readonly string[],
) => Promise<boolean | void>;

export function isGroupShape(shape: { type: string }): boolean {
  return shape.type.toLowerCase() === "group";
}

/**
 * Call `visit` for every non-group shape inside `groupShape`, in collection
 * order, descending into nested groups up to `maxDepth` levels.
 */
export async function forEachShapeInGroup(
  context: PowerPoint.RequestContext,
  groupShape: PowerPoint.Shape,
  visit: GroupShapeVisitor,
  maxDepth: number = MAX_GROUP_DEPTH,
): Promise<GroupWalkResult> {
  let skippedDeeper = false;
  let stoppedEarly = false;

  // Returns false once the visitor has asked to stop, so every level unwinds.
  const walk = async (group: PowerPoint.Shape, level: number, parents: readonly string[]): Promise<boolean> => {
    const groupPath = [...parents, group.id];
    const children = group.group.shapes;
    children.load("items/id,items/type");
    await context.sync();
    for (const child of children.items) {
      if (!isGroupShape(child)) {
        if ((await visit(child, level, groupPath)) === false) {
          stoppedEarly = true;
          return false;
        }
      } else if (level < maxDepth) {
        if (!(await walk(child, level + 1, groupPath))) return false;
      } else {
        skippedDeeper = true;
      }
    }
    return true;
  };

  await walk(groupShape, 1, []);
  return { skippedDeeper, stoppedEarly };
}
